import React, { CSSProperties, useState } from "react";
import { OnboardingStyles } from "features/onboarding/common/styles";
import Text from "common/Typography/Text";
import styled, { useTheme } from "styled-components";
import useMedia from "utils/hooks/useMedia";
import { TABLET } from "common/constants";
import { setExpandedArray } from "@shared/features/onboarding/utils";
import { ExtraInfoType } from "@shared/features/onboarding/types";
import { DemoAccountIcon } from "common/Icons/Icons";
import Title from "common/Typography/Title";
import { typography } from "@shared/common/Themes/typography";
import useConnectExchangeExtraInfo from "features/portfolio/CreateAccount/useConnectExchangeExtraInfo";

interface ConnectExchangeExtraInfoProps {
  primaryExchange?: string;
  isQuickConnect?: boolean;
}

const ConnectExchangeExtraInfo = ({
  primaryExchange,
  isQuickConnect,
}: ConnectExchangeExtraInfoProps) => {
  const theme = useTheme();
  const isTablet = useMedia([`(max-width: ${TABLET})`], [true], false);
  const [expanded, setExpanded] = useState<boolean[]>([false, false, false]);
  const [demoExpanded, setDemoExpanded] = useState(false);

  const {
    isDemoWorkspace,
    extraInfoToTitle,
    extraInfoToDescription,
    demoAccountExtraInfo,
  } = useConnectExchangeExtraInfo({ primaryExchange, isQuickConnect });

  if (isTablet) {
    if (isDemoWorkspace) {
      return (
        <CardsWrapper style={{ marginTop: 8 }}>
          <OnboardingStyles.ExpandingCard
            expanded={demoExpanded}
            setExpanded={setDemoExpanded}
            showArrow={true}
            children={
              <OnboardingStyles.ExpandingCardChildrenText>
                {demoAccountExtraInfo.title}
              </OnboardingStyles.ExpandingCardChildrenText>
            }
            collapsibleContent={demoAccountExtraInfo.content}
          />
        </CardsWrapper>
      );
    }
    return (
      <CardsWrapper>
        {Object.values(ExtraInfoType).map((extraInfo, index) => (
          <OnboardingStyles.ExpandingCard
            key={index}
            expanded={expanded[index]}
            setExpanded={(value) =>
              setExpandedArray(expanded, setExpanded, index, value)
            }
            showArrow={true}
            children={
              <OnboardingStyles.ExpandingCardChildrenText>
                {extraInfoToTitle[extraInfo]}
              </OnboardingStyles.ExpandingCardChildrenText>
            }
            collapsibleContent={
              <Text as="div">{extraInfoToDescription[extraInfo]}</Text>
            }
          />
        ))}
      </CardsWrapper>
    );
  }

  const getExtraInfo = (extraInfo: ExtraInfoType, style?: CSSProperties) => {
    return (
      <>
        <OnboardingStyles.BoldTitle style={style}>
          {extraInfoToTitle[extraInfo]}
        </OnboardingStyles.BoldTitle>
        {extraInfoToDescription[extraInfo]}
      </>
    );
  };

  if (isDemoWorkspace) {
    return (
      <OnboardingStyles.InfoBackground>
        <DemoAccountIcon
          style={{ width: 46, height: 40, marginBottom: 12 }}
          colors={theme.colors.demoTagColors.reverse()}
        />
        <Title level={3} $weight={typography.fontWeights.BOLD}>
          {demoAccountExtraInfo.title}
        </Title>
        {demoAccountExtraInfo.content}
      </OnboardingStyles.InfoBackground>
    );
  }

  return (
    <>
      <OnboardingStyles.InfoBackground style={{ marginBottom: 40 }}>
        {getExtraInfo(ExtraInfoType.CONNECT_API)}
      </OnboardingStyles.InfoBackground>
      <OnboardingStyles.InfoBackground>
        {getExtraInfo(ExtraInfoType.NO_EXCHANGE_ACCOUNT)}
        {getExtraInfo(ExtraInfoType.API_KEYS, { marginTop: 24 })}
      </OnboardingStyles.InfoBackground>
    </>
  );
};

export default ConnectExchangeExtraInfo;

const CardsWrapper = styled.div`
  margin-top: 16px;
  width: 100%;
`;
